import { useState, useEffect, useCallback } from "react";
import { useSession } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Sidebar from "../components/layout/Sidebar";
import api from "../services/api";
import { usePlans } from "../context/PlanContext";

interface Student {
  id: string;
  name: string;
  email: string;
  membership: string;
  points: number;
  createdAt: string;
  _count: {
    submissions: number;
  };
}

export default function StudentAssessmentPage() {
  const { status } = useSession();
  const { plans: globalPlans, getPlanByName } = usePlans();
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("NAME_ASC");

  const fetchStudents = useCallback(async () => {
    try {
      const res = await api.get(`/coach/students?t=${Date.now()}`);
      setStudents(res.data);
    } catch (err: any) {
      console.error("Failed to fetch students:", err);
      setError(`Error: ${err.response?.data?.error || err.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "unauthenticated") {
      navigate("/login");
    }
  }, [status, navigate]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const filteredStudents = students
    .filter(s => {
      const matchesSearch = s.name.toLowerCase().includes(search.toLowerCase()) || 
                           s.email.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === "ALL" || s.membership === filter;
      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      if (sortBy === "NAME_ASC") return a.name.localeCompare(b.name);
      if (sortBy === "NAME_DESC") return b.name.localeCompare(a.name);
      if (sortBy === "POINTS_DESC") return b.points - a.points;
      if (sortBy === "POINTS_ASC") return a.points - b.points;
      if (sortBy === "TASKS_DESC") return (b._count?.submissions || 0) - (a._count?.submissions || 0);
      return 0;
    });

  const getTierStyle = (tier: string) => {
    const plan = getPlanByName(tier);
    if (plan && plan.icon_color) {
      return { backgroundColor: plan.icon_color, color: "#ffffff" };
    }
    switch (tier?.toUpperCase()) {
      case "VIP": return { backgroundColor: "#06b6d4", color: "#ffffff" };
      case "PREMIUM": return { backgroundColor: "#f59e0b", color: "#ffffff" };
      default: return { backgroundColor: "#64748b", color: "#ffffff" };
    }
  };

  if (status === "loading") return null;

  return (
    <div className="min-h-screen bg-background text-on-background flex flex-col h-screen overflow-hidden">
      <Header />
      <Sidebar />

      <main className="flex-1 ml-0 md:ml-20 pt-16 h-full overflow-y-auto custom-scrollbar">
        <div className="max-w-7xl mx-auto p-4 sm:p-8 lg:p-12 pb-32">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-8 sm:mb-12">
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-black text-on-surface tracking-tighter mb-2">
                Student Assessment
              </h1>
              <div className="flex items-center gap-4">
                <p className="text-on-surface-variant font-medium text-xs sm:text-sm opacity-60">
                  Monitor and evaluate student progress across all courses.
                </p>
                <button 
                  onClick={() => { setLoading(true); fetchStudents(); }}
                  className="p-2 bg-surface-container-high rounded-xl hover:bg-primary/10 transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">refresh</span>
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <div className="relative group w-full sm:w-auto flex-1 sm:flex-none">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/40 transition-colors group-focus-within:text-primary">
                  search
                </span>
                <input 
                  type="text" 
                  placeholder="Search students..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-12 pr-6 py-3.5 bg-surface-container-high text-on-surface rounded-2xl border border-outline-variant/10 w-full sm:w-64 outline-none focus:ring-2 focus:ring-primary/20 transition-all text-xs font-bold"
                />
              </div>

              <select 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-6 py-3.5 bg-surface-container-high text-on-surface rounded-2xl border border-outline-variant/10 outline-none focus:ring-2 focus:ring-primary/20 transition-all text-[10px] font-black uppercase tracking-widest cursor-pointer w-full sm:w-auto"
              >
                <option value="NAME_ASC">Name (A-Z)</option>
                <option value="NAME_DESC">Name (Z-A)</option>
                <option value="POINTS_DESC">Highest XP</option>
                <option value="POINTS_ASC">Lowest XP</option>
                <option value="TASKS_DESC">Most Active</option>
              </select>
            </div>
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-500 text-xs font-bold uppercase tracking-widest">
              <span className="material-symbols-outlined text-sm">error</span>
              {error}
            </div>
          )}

          {/* Filter Chips */}
          <div className="flex flex-wrap gap-2.5 sm:gap-3 mb-8 sm:mb-12">
            {["ALL", "FREE", ...globalPlans.map(p => p.name.toUpperCase())].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-5 py-2 sm:px-6 sm:py-2.5 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all ${
                  filter === f 
                    ? "bg-primary text-white shadow-lg shadow-primary/20 scale-105" 
                    : "bg-surface-container-high text-on-surface-variant/60 hover:bg-surface-container-highest"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8 sm:mb-12">
            <div className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-3">Total Students</p>
              <h2 className="text-3xl sm:text-4xl font-black text-on-surface">{students.length}</h2>
            </div>
            <div className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-3">Total Activities</p>
              <h2 className="text-3xl sm:text-4xl font-black text-on-surface">
                {students.reduce((acc, s) => acc + (s._count?.submissions || 0), 0)}
              </h2>
            </div>
            <div className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 shadow-sm">
              <p className="text-[10px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-3">Avg. XP</p>
              <h2 className="text-3xl sm:text-4xl font-black text-on-surface">
                {students.length > 0 ? Math.round(students.reduce((acc, s) => acc + s.points, 0) / students.length) : 0}
              </h2>
            </div>
          </div>

          {/* Student Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-surface-container-low h-64 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 animate-pulse" />
              ))
            ) : filteredStudents.length > 0 ? (
              filteredStudents.map((student) => (
                <div 
                  key={student.id} 
                  onClick={() => navigate(`/coach/student-assessment/${student.id}`)}
                  className="bg-surface-container-low p-6 sm:p-8 rounded-[1.75rem] sm:rounded-[2.5rem] border border-outline-variant/10 hover:border-primary/30 hover:shadow-2xl hover:shadow-primary/5 transition-all cursor-pointer group flex flex-col items-center text-center relative overflow-hidden min-h-[280px] justify-between"
                >
                  <div 
                    className="absolute top-0 right-0 px-4 py-2 rounded-bl-2xl sm:rounded-bl-3xl text-[9px] font-black uppercase tracking-widest"
                    style={getTierStyle(student.membership || "FREE")}
                  >
                    {student.membership || "FREE"}
                  </div>

                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-[1.5rem] sm:rounded-[2rem] bg-on-surface text-surface flex items-center justify-center font-black text-2xl sm:text-3xl mb-4 sm:mb-6 shadow-xl group-hover:bg-primary transition-all duration-500 group-hover:rotate-6 group-hover:scale-110 ring-4 ring-surface-container-highest flex-shrink-0">
                    {student?.name?.[0] || "U"}
                  </div>

                  <div className="w-full">
                    <h3 className="text-base sm:text-lg font-black text-on-surface tracking-tighter mb-1 truncate w-full">{student.name}</h3>
                    <p className="text-[10px] font-medium text-on-surface-variant/40 mb-6 truncate w-full">{student.email}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-4 w-full mt-auto">
                    <div className="bg-surface-container-highest/40 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-outline-variant/10 group-hover:bg-surface-container-highest transition-colors">
                      <p className="text-[8px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-1">XP Points</p>
                      <p className="text-xs sm:text-sm font-black text-on-surface">{student.points.toLocaleString()}</p>
                    </div>
                    <div className="bg-surface-container-highest/40 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-outline-variant/10 group-hover:bg-surface-container-highest transition-colors">
                      <p className="text-[8px] font-black uppercase tracking-widest text-on-surface-variant/40 mb-1">Tasks</p>
                      <p className="text-xs sm:text-sm font-black text-on-surface">{student._count?.submissions || 0}</p>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-primary opacity-0 group-hover:opacity-100 transition-all translate-y-4 group-hover:translate-y-0">
                    View Assessment <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-20 text-center flex flex-col items-center justify-center opacity-30">
                <span className="material-symbols-outlined text-6xl mb-4">person_off</span>
                <p className="text-xs font-black uppercase tracking-widest">No students found</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
